/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Select } from '../../../../forms/elements/select/Select';
import { Option } from '../../../../forms/elements/select/Option';
import * as R from 'ramda';
import { Row } from '../../../../layout/flexbox';
import { Loader } from '../../../../forms/elements/loader/Loader';
import { isNilOrEmpty } from '../../../../language/module';
import { ROW_SPACING } from '../../../../forms/styles';
import LabelGroup from '../../../../forms/elements/LabelGroup';
import { MediumText } from '../../../../styles/styles';
import { QuickEntitlement } from '../../models';
import ImmutableMap from '../../../../language/ImmutableMap';
import { EntitlementType } from '../../../../entitlementTypes/entitlementType';
import { getDescription, getSeason } from '../../module';
import { getResource } from '../../../../language/resources/getResource';


const RESORT_LOADER_SIZE  = '36px';

const LABEL_GROUP = css`
  flex-direction: row;
  align-items: baseline;
  margin-bottom: 0;
`;

const ResortSelectorLoader = css`
  margin: 0;
  align-self: center;
  height: ${RESORT_LOADER_SIZE};
  width: ${RESORT_LOADER_SIZE};
  display: inline-block;
`

const DropdownSelect = styled(Select)`
  height: 40px;
  width: 350px;
  margin-left: 10px;
  ${MediumText};
  white-space: nowrap;
  display: inline-block;
  flex-basis: 350px;
  
  option {
    ${MediumText};
  }
` as any;


export type ResortDropdownItem = {
  name: string;
  resortAbbrev: string;
  entitlementId?: number;
  totalDays?: number;
  usedDays?: number;
};


/**
 * PassResortSelector: a component that renders drop-downs for a guest's passes and those passes' resorts. Does not
 * track its own state -- you have to do that.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class PassResortSelector extends React.PureComponent<{
  passes: QuickEntitlement[] | undefined,
  allEntitlementTypes: ImmutableMap<EntitlementType>,
  resorts: ResortDropdownItem[] | undefined,

  isLoadingResorts: boolean,

  selectedPass: number,
  selectedResort: string,
  resources:Record<string, any>

  /**
   * Called when the currently-selectedItem pass changes. Returns `'-1'` if no selection is made
   */
  onPassChange: (newPass: number) => void,
  /**
   * Called when the currently-selectedItem resort changes. Returns `'-1'` if no selection is made
   */
  onResortChange: (newResort: string) => void,
}> {
  private onPassChange = event => {
    const newValue = R.path<number>([ 'target', 'value' ], event);

    if (!newValue) {
      return;
    }

    this.props.onPassChange(+newValue);
  };


  private onResortChange = event => {
    const newValue = R.path<string>([ 'target', 'value' ], event);

    if (!newValue) {
      return;
    }

    this.props.onResortChange(newValue);
  };


  private passOptionsFor =
    (passes: QuickEntitlement[] | undefined, allEntitlementTypes: ImmutableMap<EntitlementType>): React.ReactNode[] => {
    if (isNilOrEmpty(passes)) {
      return [ <Option key={-1} value={'-1'} disabled={true}>{getResource(this.props.resources, 'PASS_RESORT_SELECTOR_NO_PASSES')}</Option> ];
    }

    return R.concat(
      [ <Option key={-1} value={'-1'} disabled={true}>{getResource(this.props.resources, 'PASS_RESORT_SELECTOR_PASSES')}</Option> ],
      passes!.map(pass =>
        <Option key={pass.entitlementId} value={pass.entitlementId}>
          {`${getSeason(pass, allEntitlementTypes)} ${getDescription(pass, allEntitlementTypes)}`}
        </Option>),
    );
  };


  private resortOptionsFor = (resorts: ResortDropdownItem[] | undefined): React.ReactNode[] | null => {
    if (isNilOrEmpty(resorts)) {
      return [ <Option value={''} key={-1} disabled={true}>{getResource(this.props.resources, 'PASS_RESORT_SELECTOR_NO_RESORTS')}</Option> ];
    }

    // Sort the resorts as they are however they came in the API
    const sortedResorts = resorts!.sort((resortA: ResortDropdownItem, resortB: ResortDropdownItem) => {
      if (resortA.name! > resortB.name!) {
        return 1;
      }
      return -1;
    });

    return R.concat(
      [ <Option key={-1} value={'-1'} disabled={true}>{getResource(this.props.resources, 'PASS_RESORT_SELECTOR_RESORTS')}</Option> ],
      sortedResorts.map(
        resort => <Option key={resort.resortAbbrev} value={resort.resortAbbrev}>
                    {resort.name}
                  </Option>,
      ),
    );
  };


  public render = () => (
    <Row justifyContent='space-between' rootCss={ROW_SPACING}>
      <LabelGroup label={getResource(this.props.resources, 'VIEW_DAYS_RESORT_SELECTOR_PASS_LABEL')} rootCss={LABEL_GROUP}>
        <DropdownSelect value={this.props.selectedPass}
                        onChange={this.onPassChange}
                        title={getResource(this.props.resources, 'VIEW_DAYS_RESORT_SELECTOR_PASS_TITLE')}
                        disabled={!this.props.passes}>
          {this.passOptionsFor(this.props.passes, this.props.allEntitlementTypes)}
        </DropdownSelect>
      </LabelGroup>

      <LabelGroup label={getResource(this.props.resources, 'VIEW_DAYS_RESORT_SELECTOR_RESORT_LABEL')} rootCss={LABEL_GROUP}>
        {
          this.props.isLoadingResorts
          ? <Loader rootCss={ResortSelectorLoader}/>
          : <DropdownSelect value={this.props.selectedResort}
                            onChange={this.onResortChange}
                            title={getResource(this.props.resources, 'VIEW_DAYS_RESORT_SELECTOR_RESORT_TITLE')}
                            disabled={!this.props.resorts}>
            {this.resortOptionsFor(this.props.resorts)}
          </DropdownSelect>
        }
      </LabelGroup>
    </Row>
  );
}
